import React, { useState, useEffect } from "react";
import {
  User,
  CreditCard,
  Building2,
  LogOut,
  ShieldCheck,
  Users,
  LifeBuoy,
  CircleDollarSign,
  CircleHelp,
} from "lucide-react";
import { useAuth } from "../../components/context/AuthContext";
import { useHelpCenter } from "../../components/context/HelpCenterContext";
import { useMobileTopNavBar } from "../../components/context/MobileTopNavBarContext";
import { useLocation, Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import ProfileTab from "./components/ProfileTab";
import PaymentsTab from "./components/PaymentsTab";
import V2SubscriptionTab from "./components/v2SubscriptionTab";
import BrandsTab from "./components/BrandsTab";
import PrivacyDataTab from "./components/PrivacyDataTab";
import UsersTab from "./components/UsersTab";
import SupportTab from "./components/SupportTab";
import toast from "react-hot-toast";
import { logApi } from '../../api';

const { createLog } = logApi;

function NavSection({ title, children }) {
  return (
    <div className="space-y-1">
      <h2 className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/45">
        {title}
      </h2>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function navItemClass(active) {
  return `flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
    active
      ? "border border-blue-400/35 bg-blue-600/30 text-white shadow-sm shadow-blue-950/20"
      : "border border-transparent text-white/70 hover:bg-white/5 hover:text-white"
  }`;
}

function LogoutFooter({ onLogoutClick }) {
  return (
    <div className="mt-auto border-t border-white/10 px-1 pt-4 md:px-2 md:pt-5">
      <button
        type="button"
        onClick={onLogoutClick}
        className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-red-400 transition-colors duration-200 hover:bg-red-500/10 hover:text-red-300"
      >
        <LogOut className="h-5 w-5 shrink-0" strokeWidth={1.75} />
        Cerrar sesión
      </button>
    </div>
  );
}

const Profile = () => {
  const { auth, logout } = useAuth();
  const { openHelpCenter } = useHelpCenter();
  const { setTopNavBarConfig, resetConfig } = useMobileTopNavBar();
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (auth !== null) {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    setTopNavBarConfig({
      hideFilters: true,
      hideBrandSelector: false,
      hideBack: false,
    });
    return () => resetConfig();
  }, [setTopNavBarConfig, resetConfig]);

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes("/profile/payments")) return "payments";
    if (path.includes("/profile/subscription")) return "v2subscription";
    if (path.includes("/profile/brands") || path.includes("/profile/connections")) return "brands";
    if (path.includes("/profile/privacy")) return "privacy";
    if (path.includes("/profile/users")) return "users";
    if (path.includes("/profile/support")) return "support";
    return "profile";
  };

  const activeTab = getActiveTab();
  // Growth partner: solo perfil propio + equipo (invitaciones). Sin marcas/
  // conexiones, pagos, privacidad ni soporte.
  const isGrowthPartner = Boolean(auth?.user?.is_growth_partner);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openHelp = (message) => {
    createLog({ level: "info", message });
    openHelpCenter();
  };

  if (loading) {
    return (
      <div className="relative isolate flex min-h-screen items-center justify-center overflow-x-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
          <div
            className="absolute inset-0 opacity-[0.3]"
            style={{
              backgroundImage:
                'radial-gradient(ellipse 80% 55% at 100% -10%, rgba(168,85,247,0.22), transparent 55%), radial-gradient(ellipse 70% 50% at 0% 100%, rgba(59,130,246,0.18), transparent 50%)',
            }}
          />
        </div>
        <div className="text-center">
          <div className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-t-4 border-blue-500 md:mb-4 md:h-16 md:w-16" />
          <p className="text-sm text-white/70 md:text-base">Cargando información...</p>
        </div>
      </div>
    );
  }

  const navigationGroups = (
    <>
      <NavSection title="Cuenta y organización">
        <Link
          to="/profile"
          onClick={() => createLog({ level: "info", message: 'Click en "Mi perfil"' })}
          className={navItemClass(activeTab === "profile")}
        >
          <User className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
          <span>Mi perfil</span>
        </Link>
        <Link
          to="/profile/users"
          onClick={() => createLog({ level: "info", message: 'Click en "Equipo"' })}
          className={navItemClass(activeTab === "users")}
        >
          <Users className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
          <span>Equipo</span>
        </Link>
        {!isGrowthPartner && (
          <Link
            to="/profile/brands"
            onClick={() => createLog({ level: "info", message: 'Click en "Marcas"' })}
            className={navItemClass(activeTab === "brands")}
          >
            <Building2 className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
            <span>Marcas</span>
          </Link>
        )}
        {!isGrowthPartner && (
          <Link
            to="/profile/privacy"
            onClick={() => createLog({ level: "info", message: 'Click en "Privacidad y datos"' })}
            className={navItemClass(activeTab === "privacy")}
          >
            <ShieldCheck className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
            <span>Privacidad y datos</span>
          </Link>
        )}
      </NavSection>

      {!isGrowthPartner && (
        <NavSection title="Pagos y planes">
          <Link
            to="/profile/subscription"
            onClick={() => createLog({ level: "info", message: 'Click en "Suscripción"' })}
            className={navItemClass(activeTab === "v2subscription")}
          >
            <CreditCard className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
            <span>Suscripción</span>
          </Link>
          <Link
            to="/profile/payments"
            onClick={() => createLog({ level: "info", message: 'Click en "Facturación"' })}
            className={navItemClass(activeTab === "payments")}
          >
            <CircleDollarSign className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
            <span>Facturación</span>
          </Link>
        </NavSection>
      )}

      {!isGrowthPartner && (
        <NavSection title="Ayuda y soporte">
          <button
            type="button"
            onClick={() => openHelp('Click "Preguntas frecuentes" en perfil')}
            className={navItemClass(false)}
          >
            <CircleHelp className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
            <span className="text-left">Preguntas frecuentes</span>
          </button>
          <Link
            to="/profile/support"
            onClick={() => createLog({ level: "info", message: 'Click en "Soporte y contacto"' })}
            className={navItemClass(activeTab === "support")}
          >
            <LifeBuoy className="h-5 w-5 shrink-0 opacity-90" strokeWidth={1.75} />
            <span>Soporte y contacto</span>
          </Link>
        </NavSection>
      )}
    </>
  );

  const routes = isGrowthPartner ? (
    <Routes>
      <Route index element={<ProfileTab onError={toast.error} onSuccess={toast.success} />} />
      <Route path="users" element={<UsersTab onError={toast.error} onSuccess={toast.success} />} />
      <Route path="*" element={<Navigate to="/profile" replace />} />
    </Routes>
  ) : (
    <Routes>
      <Route index element={<ProfileTab onError={toast.error} onSuccess={toast.success} />} />
      <Route path="subscription" element={<V2SubscriptionTab onError={toast.error} onSuccess={toast.success} />} />
      <Route path="payments" element={<PaymentsTab onError={toast.error} onSuccess={toast.success} />} />
      <Route path="brands" element={<BrandsTab />} />
      <Route path="connections" element={<Navigate to="/profile/brands" replace />} />
      <Route path="privacy" element={<PrivacyDataTab onError={toast.error} onSuccess={toast.success} />} />
      <Route path="users" element={<UsersTab onError={toast.error} onSuccess={toast.success} />} />
      <Route path="support" element={<SupportTab />} />
    </Routes>
  );

  return (
    <div className="relative isolate min-h-screen overflow-x-hidden text-white app-dark-input-scope">
      {/* Velo decorativo ligero: el gradiente base viene del layout (ProtectedLayout); aquí solo halos + rejilla */}
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        <div
          className="absolute inset-0 opacity-[0.32]"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 80% 55% at 100% -10%, rgba(168,85,247,0.28), transparent 55%),
              radial-gradient(ellipse 70% 50% at 0% 100%, rgba(59,130,246,0.22), transparent 50%),
              radial-gradient(ellipse 50% 40% at 70% 60%, rgba(99,102,241,0.1), transparent 45%)
            `,
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <div className="relative z-0 mx-auto flex max-w-full flex-col md:max-w-7xl md:flex-row md:gap-8 lg:gap-10">
        {/* Sidebar desktop (misma línea visual que Sidebar / dropdowns) */}
        <aside className="relative z-0 hidden min-h-screen w-72 shrink-0 flex-col border-r border-white/10 bg-transparent lg:w-80 md:flex md:px-3 md:py-8">
          <div className="relative z-10 px-3 pb-6">
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
              <span className="bg-gradient-to-r from-fuchsia-400 to-blue-700 bg-clip-text text-transparent">
                Configuración
              </span>
            </h1>
            <p className="mt-1 text-sm text-white/60">Perfil, equipo y facturación</p>
          </div>
          <div className="relative z-10 flex flex-1 flex-col gap-8 overflow-y-auto px-1 pb-4">
            {navigationGroups}
          </div>
          <div className="relative z-10">
            <LogoutFooter onLogoutClick={() => setShowLogoutConfirm(true)} />
          </div>
        </aside>

        {/* Columna principal: móvil (nav + contenido) y desktop (solo contenido) */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="md:hidden">
            <div className="flex items-start justify-between gap-3 border-b border-white/10 px-4 py-4">
              <div>
                <h1 className="text-xl font-bold">
                  <span className="bg-gradient-to-r from-fuchsia-400 to-blue-700 bg-clip-text text-transparent">
                    Configuración
                  </span>
                </h1>
                <p className="mt-0.5 text-xs text-white/60">Perfil, equipo y facturación</p>
              </div>
            </div>
            <nav className="max-h-[min(52vh,420px)] overflow-y-auto border-b border-white/10 bg-transparent px-3 pb-4">
              <div className="flex flex-col gap-6 pt-2">{navigationGroups}</div>
            </nav>
            <div className="px-3 pb-4 pt-2">
              <LogoutFooter onLogoutClick={() => setShowLogoutConfirm(true)} />
            </div>
          </div>

          <main className="w-full flex-1 px-4 pb-24 pt-4 md:px-0 md:pb-10 md:pt-8">
            {routes}
          </main>
        </div>
      </div>

      {showLogoutConfirm && (
        <div className="modal-safe-area fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-sm rounded-2xl border border-white/20 bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-950/95 p-6 shadow-2xl backdrop-blur-xl">
            <h3 className="mb-2 text-lg font-bold text-white">¿Cerrar sesión?</h3>
            <p className="mb-6 text-sm text-white/65">Se cerrará tu sesión en EsporAI</p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 rounded-xl border border-white/10 bg-white/10 px-4 py-3 font-medium text-white transition-colors hover:bg-white/15"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="flex-1 rounded-xl bg-red-600 px-4 py-3 font-medium text-white transition-colors hover:bg-red-700"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
